/**
 * PermissionDeniedView.tsx - Permission Denied Display
 *
 * Full-page view shown when the user does not have access to the circle.
 * Includes a "Request Access" button to request circle membership.
 */

import { useState } from "react";
import { Box, VStack, Text, Heading, Button, Spinner } from "@chakra-ui/react";
import { LuShieldAlert, LuCircleCheck } from "react-icons/lu";
import { Trans, useTranslation } from "react-i18next";

interface PermissionDeniedViewProps {
    circleName?: string;
    requestGroupAccess?: () => Promise<boolean>;
}

export const PermissionDeniedView = ({ circleName, requestGroupAccess }: PermissionDeniedViewProps) => {
    const { t, i18n } = useTranslation();
    const [requesting, setRequesting] = useState(false);
    const [requested, setRequested] = useState(false);
    const [requestError, setRequestError] = useState<string | null>(null);

    const handleRequestAccess = async () => {
        if (!requestGroupAccess || requesting) return;
        setRequesting(true);
        setRequestError(null);
        try {
            await requestGroupAccess();
            setRequested(true);
        } catch {
            setRequestError(t("request_access_error"));
        } finally {
            setRequesting(false);
        }
    };

    return (
        <Box
            width="100%"
            height="100dvh"
            display="flex"
            alignItems="center"
            justifyContent="center"
            background="white"
        >
            <VStack gap={4} textAlign="center" paddingX={8} maxWidth="400px">
                <Box as={LuShieldAlert} boxSize="48px" color="red.400" />
                <Heading size="md" color="gray.800">
                    {t("permission_denied_title")}
                </Heading>
                <Text
                    fontSize="sm"
                    color="gray.600"
                    fontFamily={['ar', 'ckb', 'ur'].includes(i18n.language)
                        ? "'Muna', 'KV-Text', sans-serif"
                        : "'KV-Text', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"}
                >
                    <Trans
                        i18nKey="permission_denied_message"
                        values={{ circleName: circleName || '' }}
                        components={{ bold: <strong /> }}
                    />
                </Text>

                {requestGroupAccess && !requested && (
                    <Button
                        onClick={handleRequestAccess}
                        disabled={requesting}
                        size="md"
                        background="var(--cair-accent)"
                        color="white"
                        _hover={{ background: "var(--cair-accent-hover)" }}
                        variant="solid"
                        marginTop={2}
                    >
                        {requesting ? <Spinner size="sm" /> : t("request_access")}
                    </Button>
                )}

                {requested && (
                    <VStack gap={2} marginTop={2}>
                        <Box as={LuCircleCheck} boxSize="24px" color="green.500" />
                        <Text fontSize="sm" color="green.600" fontWeight="medium">
                            {t("request_access_success")}
                        </Text>
                    </VStack>
                )}

                {requestError && (
                    <Text fontSize="sm" color="red.500" marginTop={1}>
                        {requestError}
                    </Text>
                )}
            </VStack>
        </Box>
    );
};
